<script setup lang="ts">
import type { Tables } from '~~/types/database.types.ts';
import uniqBy from 'lodash.uniqby';
import countBy from 'lodash.countby';
import {
  defineOgImageComponent,
  useAsyncData,
  computed,
  useHead,
  useI18n,
  useRoute,
  useSupabaseClient,
  useToast,
} from '#imports';
import MPCreatorCard from '~/components/MPCreatorCard/MPCreatorCard.vue';

const { showError } = useToast();
const { t } = useI18n();

const route = useRoute();
const supabase = useSupabaseClient();
const { data: destination, pending: destinationPending } = await useAsyncData(
  'destination:{id}',
  async (): Promise<Tables<'cities'>> => {
    const { data, error } = await supabase
      .from('cities')
      .select('*')
      .eq('id', route.params.id)
      .single();
    if (error) showError('Could not fetch destination');
    return data;
  },
);

const { data: spots } = await useAsyncData('destination_spots:{id}', async () => {
  const { data, error } = await supabase
    .from('spots')
    .select(`
      *,
      categories:category_id (
        id,
        name,
        icon
      ),
      cities:cities_id (
        id,
        name
      ),
      maps:map_id (
        *,
        profiles:user_id (
          id,
          profile_image_url,
          mappr_handle,
          display_name,
          summary,
          maps_count:maps(count)
        )
      )
    `)
    .eq('cities_id', route.params.id)
    .eq('is_published', true)
    .order('created_at', { ascending: false });

  if (error) showError('Failed to fetch spots')
  return data
})

const allCategories = uniqBy(
  spots?.value?.flatMap((spot: Tables<'spots'>) => spot.categories)
    .filter((category) => category !== null && category !== undefined),
  'id')


const categoriesWithMultipleSpots = computed(() => {
  const spotCategories = spots?.value?.flatMap((spot: Tables<'spots'>) => spot.categories).filter(Boolean);

  if (!spotCategories?.length) return [];
  const haveMultipleSpots = countBy(spotCategories, 'id');
  const res = spotCategories.filter((category) => category && category.id && haveMultipleSpots[category.id] > 2);
  return uniqBy(res, 'id');
});


useHead(() => ({
  title: `${destination.value.name} - ${destination.value.country}`,
  meta: [
    {
      name: 'description',
      content: 'Explore the world and discover new places to visit',
    },
  ],
}));

defineOgImageComponent('Default', {
  title: `${destination.value.name} - ${destination.value.country}`,
  description: destination.value.summary,
  imagePath: destination.value.cover_image_url,
});
</script>

<template>
  <div>
    <div v-if="destinationPending">
      <MPLoader label="Loading destination" />
    </div>
    <div v-if="destination && !destinationPending" class="destination-page">
      <MPSectionHeader
        :title="destination.name"
        :subtitle="destination.country"
      />
      <div v-if="allCategories" class="categories-list">
        <MPCategoryList :categories="allCategories" />
      </div>
      <div class="destination-header">
        <MPImg :src="destination.cover_image_url" class="destination-image" :width="1500"/>
      </div>
      <section class="destination-carousel-wrapper">
        <MPSectionHeader
          title="Explore curated Spots by Local experts"
          summary="Unlock insights from real creators, discover hidden gems and secret spots that go beyond the usual travel guides."
          subtitle="Your local guide"
        />
        <MPCardCarousel
          v-if="spots && spots.length > 4"
          :slides-per-view="5"
        >
          <template #slide-0>
            <MPSpotCard :spot="spots[0]"/>
          </template>
          <template #slide-1>
            <MPSpotCard :spot="spots[1]"/>
          </template>
          <template #slide-2>
            <MPCreatorCard :creator="spots[2].maps.profiles" />
          </template>
          <template #slide-3>
            <MPSpotCard :spot="spots[3]"/>
          </template>
          <template #slide-4>
            <MPSpotCard :spot="spots[4]"/>
          </template>
        </MPCardCarousel>
      </section>

      <h3>Spots</h3>
      <p class="summary">Discover unique spots in {{ destination.name }}</p>

       <MPViewportSpill show-controls class="spots">
        <LazyMPSpotCard v-for="spot in spots" :key="spot.id" :spot="spot" actions/>
      </MPViewportSpill>

      <div v-if="categoriesWithMultipleSpots?.length > 0">
        <div v-for="category in categoriesWithMultipleSpots" :key="category.id" class="category-section">
          <h3>{{ category.name }}</h3>
          <p class="summary">{{ category.name }} spots in {{ destination.name }}</p>
          <MPViewportSpill show-controls class="spots">
            <LazyMPSpotCard v-for="spot in spots.filter((spot) => spot.categories?.id === category.id)" :key="spot.id" :spot="spot" actions/>
          </MPViewportSpill>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/typography.pcss';
@import '@/assets/css/layouts.pcss';
@import '@/assets/css/breakpoints.pcss';
$gridGap:1rem;
.destination-page {
  @mixin layout-wrapper-wide;
  margin: 1.5rem auto;
  padding: 1rem;
  @mixin screen sm {
    padding: 2rem 1rem;
  }
}

.destination-carousel-wrapper {
  margin: 2rem 0;
  @mixin screen md {
    margin: 6rem 0 10rem 0;
  }
}

.destination-header {
  width: 100%;
  position: relative;
  img {
    aspect-ratio: 16 / 7;
    object-fit: cover;
    width: 100%;
    border-radius: 1rem;
  }
}

.categories-list {
  margin: 0 auto 2rem;
  width: fit-content;
}

h3 {
  margin-bottom: 0;
}

p.summary {
  color: var(--color-slate-400);
  @mixin typeface-regular;
  margin-bottom: 1.5rem;
}

.destination-info {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 3px);
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 1rem;
  justify-content: end;
  padding: 1rem 2rem;
  background: linear-gradient(to top right, rgba(0, 0, 0, 0.5), transparent);
  @mixin screen md {
    padding: 2rem 2rem;
  }
  h1 {
    @mixin typeface-title;
    color: var(--color-white);
    margin: 0.5rem 0 0 0;
  }
  p {
    color: var(--color-rose-400);
    margin: 0;
  }
}


.maps-list + h3 {
  margin-top: 4rem;
}

.maps {
  width: 100%;
  gap: $gridGap;
  &:deep(.map-card) {
    width: calc(($layoutWide / 5) - $gridGap);
  }
}
.spots {
  width: 100%;
  gap: $gridGap;
  &:deep(.spot) {
    width: calc(($layoutWide / 5) - $gridGap);
  }
}

</style>
